'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from './AuthContext';

export interface FavoriteItem {
  id: string;
  itemId: string;
  itemType: 'act' | 'stage';
  eventId: string;
  itemName: string;
}

interface FavoritesContextType {
  favorites: FavoriteItem[];
  isFavorite: (itemId: string, eventId: string) => boolean;
  toggleFavorite: (itemId: string, itemType: 'act' | 'stage', eventId: string, itemName: string) => Promise<void>;
  loading: boolean;
}

const FavoritesContext = createContext<FavoritesContextType>({
  favorites: [],
  isFavorite: () => false,
  toggleFavorite: async () => {},
  loading: false,
});

export const useFavorites = () => useContext(FavoritesContext);

const LOCAL_FAVORITES_KEY = 'mps_favorites_local';

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const supabase = createClient();

  // Load favorites from Supabase or localStorage
  const loadFavorites = useCallback(async () => {
    if (user) {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('favorites')
          .select('*')
          .eq('user_id', user.id);
        if (!error && data) {
          setFavorites(data.map((f: any) => ({
            id: f.id,
            itemId: f.item_id,
            itemType: f.item_type,
            eventId: f.event_id,
            itemName: f.item_name,
          })));
        }
      } catch {
        // fallback to local
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const stored = localStorage.getItem(LOCAL_FAVORITES_KEY);
        if (stored) setFavorites(JSON.parse(stored));
      } catch {
        setFavorites([]);
      }
    }
  }, [user]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const isFavorite = useCallback((itemId: string, eventId: string) => {
    return favorites.some(f => f.itemId === itemId && f.eventId === eventId);
  }, [favorites]);

  const toggleFavorite = useCallback(async (
    itemId: string,
    itemType: 'act' | 'stage',
    eventId: string,
    itemName: string
  ) => {
    const exists = favorites.some(f => f.itemId === itemId && f.eventId === eventId);

    if (user) {
      if (exists) {
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('item_id', itemId)
          .eq('event_id', eventId);
        if (!error) {
          setFavorites(prev => prev.filter(f => !(f.itemId === itemId && f.eventId === eventId)));
        }
      } else {
        const { data, error } = await supabase
          .from('favorites')
          .insert({ user_id: user.id, item_id: itemId, item_type: itemType, event_id: eventId, item_name: itemName })
          .select()
          .single();
        if (!error && data) {
          setFavorites(prev => [...prev, {
            id: data.id,
            itemId: data.item_id,
            itemType: data.item_type,
            eventId: data.event_id,
            itemName: data.item_name,
          }]);
        }
      }
    } else {
      // Local storage fallback
      let updated: FavoriteItem[];
      if (exists) {
        updated = favorites.filter(f => !(f.itemId === itemId && f.eventId === eventId));
      } else {
        updated = [...favorites, {
          id: `local-${Date.now()}`,
          itemId,
          itemType,
          eventId,
          itemName,
        }];
      }
      setFavorites(updated);
      try {
        localStorage.setItem(LOCAL_FAVORITES_KEY, JSON.stringify(updated));
      } catch {}
    }
  }, [user, favorites, supabase]);

  return (
    <FavoritesContext.Provider value={{ favorites, isFavorite, toggleFavorite, loading }}>
      {children}
    </FavoritesContext.Provider>
  );
}
